import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowRight, Sparkles, Upload } from 'lucide-react';
import { PublicShell } from './PublicShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { publicUrl, hreflangAlternates, activeLang, ogLocaleOf } from '../lib/publicSeo';
import type { ImportSource } from '../data/importSources';

const OG_IMAGE = 'https://markflow.fyi/og.png';

/**
 * Shared SEO landing template for `/import-from-<slug>` pages. One template,
 * per-source copy — the source differs only by logo, name, and export steps.
 * Structural model: PocketLandingPage (hero + wedges + final CTA), minus the
 * bespoke comparison table.
 */
export function ImportLanding({ source }: { source: ImportSource }) {
  const { t, i18n } = useTranslation();
  const lang = activeLang(i18n.resolvedLanguage);
  const route = `/import-from-${source.slug}`;
  const pageUrl = publicUrl(route, lang);

  const name = t(`importLanding.sources.${source.id}.name`);
  const title = t(`importLanding.sources.${source.id}.meta.title`);
  const description = t(`importLanding.sources.${source.id}.meta.description`);

  usePageMeta({
    title,
    description,
    canonical: pageUrl,
    ogTitle: title,
    ogDescription: description,
    ogImage: OG_IMAGE,
    ogUrl: pageUrl,
    htmlLang: lang,
    ogLocale: ogLocaleOf(lang),
    ogLocaleAlternate: ogLocaleOf(lang === 'uk' ? 'en' : 'uk'),
    alternates: hreflangAlternates(route),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: title,
      description,
      step: (t(`importLanding.sources.${source.id}.steps`, { returnObjects: true }) as string[]).map(
        (text, i) => ({ '@type': 'HowToStep', position: i + 1, text }),
      ),
      url: pageUrl,
      image: OG_IMAGE,
    },
  });

  const steps = t(`importLanding.sources.${source.id}.steps`, { returnObjects: true }) as string[];
  const importHref = source.importVariant ? `/import?variant=${source.importVariant}` : '/import';

  return (
    <PublicShell>
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-16">

          {/* Hero */}
          <section className="space-y-6 text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-violet-50 dark:bg-violet-950/40 px-3 py-1 text-xs font-medium text-violet-700 dark:text-violet-300">
              <img src={source.logo} alt="" className="w-4 h-4" aria-hidden="true" />
              {t('importLanding.hero.eyebrow', { name })}
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50 leading-tight">
              {t('importLanding.hero.title', { name })}
            </h1>
            <p className="text-lg sm:text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              {t('importLanding.hero.subtitle', { name })}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Link
                href={`/?from=${source.slug}`}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 transition-colors shadow-sm w-full sm:w-auto"
              >
                {t('importLanding.hero.ctaPrimary')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href={importHref}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors w-full sm:w-auto"
              >
                <Upload className="w-4 h-4" />
                {t('importLanding.hero.ctaImport')}
              </Link>
            </div>
          </section>

          {/* Export steps */}
          <section className="space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {t('importLanding.steps.title', { name })}
            </h2>
            <ol className="space-y-3">
              {steps.map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
                >
                  <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 text-sm font-semibold">
                    {i + 1}
                  </span>
                  <span className="text-sm sm:text-base text-zinc-700 dark:text-zinc-200 leading-relaxed pt-0.5">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
            <div className="rounded-xl border border-violet-200 dark:border-violet-900/50 bg-violet-50/50 dark:bg-violet-950/20 p-5 space-y-1.5">
              <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">
                {t('importLanding.steps.importTitle')}
              </h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                {t('importLanding.steps.importBody')}
              </p>
              <Link
                href={importHref}
                className="inline-flex items-center gap-1.5 pt-1 text-sm font-medium text-violet-700 dark:text-violet-300 hover:underline"
              >
                {t('importLanding.hero.ctaImport')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>

          {/* Wedges */}
          <section className="space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {t('importLanding.wedges.title')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(['ai', 'crossBrowser', 'privacy'] as const).map((key) => (
                <div
                  key={key}
                  className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 space-y-2"
                >
                  <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">
                    {t(`importLanding.wedges.${key}.title`)}
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    {t(`importLanding.wedges.${key}.body`)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-500 p-8 sm:p-10 text-center space-y-4">
            <Sparkles className="w-6 h-6 text-white/90 mx-auto" />
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
              {t('importLanding.finalCta.title', { name })}
            </h2>
            <p className="text-violet-100 max-w-lg mx-auto">
              {t('importLanding.finalCta.body')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Link
                href={`/?from=${source.slug}`}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-violet-700 bg-white hover:bg-zinc-50 transition-colors shadow-sm w-full sm:w-auto"
              >
                {t('importLanding.finalCta.ctaPrimary')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href={importHref}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white border border-white/40 hover:bg-white/10 transition-colors w-full sm:w-auto"
              >
                <Upload className="w-4 h-4" />
                {t('importLanding.finalCta.ctaImport')}
              </Link>
            </div>
          </section>

        </div>
      </main>
    </PublicShell>
  );
}
